import { useState, useEffect, useCallback } from 'react';

/**
 * Executor Kit gating.
 *
 * Only the `first-30-days` doc is fully free. Every other kit doc — plus the
 * combined full-kit PDF — is behind an email capture that also enrolls the
 * reader into the "executor" outreach drip.
 *
 * We persist the unlock in localStorage so a reader only sees the modal once
 * per browser. The value stored is the signed token returned by the backend;
 * we don't verify the signature client-side (server enforces on next visit if
 * we ever add server-gated content), but its presence is enough for the UI.
 *
 * Cross-consumer sync: The page grid AND the modal each call `useKitUnlock()`
 * as independent React state instances. The browser's `storage` event only
 * fires in OTHER tabs, so a same-tab unlock (the common case: modal writes,
 * then the parent page needs to re-render without lock badges) previously
 * left the grid stale until the user reloaded. We now maintain a module-level
 * subscriber Set and fan out an update to every mounted consumer whenever
 * `setUnlocked` fires.
 */

const STORAGE_KEY = 'epf.executor_kit_unlocked';

export const FREE_KIT_SLUGS = new Set(['first-30-days']);

export const isKitSlugFree = (slug) => FREE_KIT_SLUGS.has(slug);

export const readUnlockState = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.email || !parsed?.token) return null;
    return parsed;
  } catch {
    return null;
  }
};

// Module-level pub/sub so every mounted `useKitUnlock()` consumer in the
// current tab re-reads localStorage after any of them writes to it.
const listeners = new Set();
const notifyLocalConsumers = () => {
  const next = readUnlockState();
  listeners.forEach((fn) => {
    try { fn(next); } catch { /* consumer unmounted mid-notify */ }
  });
};

export const useKitUnlock = () => {
  const [state, setState] = useState(() => readUnlockState());

  useEffect(() => {
    listeners.add(setState);
    // Cross-tab sync via the browser storage event (does NOT fire in the
    // tab that wrote localStorage — that's why the module-level pub/sub
    // above exists).
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setState(readUnlockState());
    };
    window.addEventListener('storage', onStorage);
    return () => {
      listeners.delete(setState);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const unlocked = !!state?.token;

  const setUnlocked = useCallback((next) => {
    if (next?.token && next?.email) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      notifyLocalConsumers();
    }
  }, []);

  return { unlocked, unlockData: state, setUnlocked };
};
